import { useParams, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { communities } from '@/mocks/communities';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import SiteHeader from '@/components/feature/SiteHeader';
import CommunityCard from '@/pages/home/components/CommunityCard';
import Footer from '@/pages/home/components/Footer';

export default function TagPage() {
  const { tag } = useParams<{ tag: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'members' | 'activity' | 'newest'>('members');

  // All unique tags for the sidebar
  const allTags = useMemo(() => {
    const tagMap: Record<string, number> = {};
    communities.forEach((c) => {
      c.tags.forEach((t) => {
        tagMap[t] = (tagMap[t] || 0) + 1;
      });
    });
    return Object.entries(tagMap)
      .sort((a, b) => b[1] - a[1])
      .map(([t, count]) => ({ tag: t, count }));
  }, []);

  const matched = useMemo(() => {
    if (!tag) return [];
    let list = communities.filter((c) =>
      c.tags.some((t) => t.toLowerCase() === tag.toLowerCase())
    );
    if (sortBy === 'members') list = [...list].sort((a, b) => b.memberCount - a.memberCount);
    else if (sortBy === 'newest') list = [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    else if (sortBy === 'activity') {
      const order = { high: 0, medium: 1, low: 2 };
      list = [...list].sort((a, b) => order[a.activityLevel] - order[b.activityLevel]);
    }
    return list;
  }, [tag, sortBy]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <SiteHeader showBack />

      <main className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-5">
          <button onClick={() => navigate('/')} className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
          <span>/</span>
          <span className="text-slate-500">Tags</span>
          <span>/</span>
          <span className="text-[#0088CC] font-medium">#{tag}</span>
        </nav>

        <div className="flex gap-6 items-start">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Page Header */}
            <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white/80 px-6 py-5 mb-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0088CC]/10 border border-[#0088CC]/20">
                      <i className="ri-hashtag text-[#0088CC] text-sm"></i>
                    </span>
                    <h1 className="text-2xl font-extrabold text-slate-800">#{tag}</h1>
                  </div>
                  <p className="text-sm text-slate-400 ml-10">
                    {matched.length} communit{matched.length !== 1 ? 'ies' : 'y'} tagged with <strong className="text-slate-600">#{tag}</strong>
                  </p>
                </div>
                {/* Sort */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400 whitespace-nowrap">Sort by:</span>
                  {([
                    { key: 'members', label: 'Most Members' },
                    { key: 'activity', label: 'Most Active' },
                    { key: 'newest', label: 'Newest' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm ${
                        sortBy === opt.key
                          ? 'bg-[#0088CC]/80 border-[#0088CC]/60 text-white'
                          : 'bg-white/50 border-[#b8d4ea]/60 text-slate-500 hover:border-[#0088CC]/30 hover:text-slate-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results */}
            {matched.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {matched.map((c) => (
                  <CommunityCard key={c.id} community={c} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center bg-white/50 backdrop-blur-md rounded-2xl border border-white/80">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#0088CC]/10 mb-4">
                  <i className="ri-hashtag text-2xl text-[#0088CC]"></i>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No communities found</h3>
                <p className="text-slate-400 text-sm mb-5">No communities are tagged with <strong>#{tag}</strong> yet.</p>
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 bg-[#0088CC] hover:bg-[#0099DD] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                  Browse All Communities
                </button>
              </div>
            )}
          </div>

          {/* Right: All Tags Panel */}
          <aside className="hidden xl:block w-64 shrink-0 sticky top-20">
            <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]">
                  <i className="ri-price-tag-3-line text-sm"></i>
                </span>
                All Tags
              </h3>
              <div className="flex flex-wrap gap-1.5 max-h-[70vh] overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
                {allTags.map(({ tag: t, count }) => (
                  <button
                    key={t}
                    onClick={() => navigate(`/tag/${t}`)}
                    className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      t.toLowerCase() === (tag ?? '').toLowerCase()
                        ? 'bg-[#0088CC] border-[#0088CC] text-white'
                        : 'bg-white/60 border-[#c5d9eb]/50 text-slate-500 hover:border-[#0088CC]/30 hover:text-[#0088CC]'
                    }`}
                  >
                    #{t}
                    <span className={`text-[10px] font-semibold ${t.toLowerCase() === (tag ?? '').toLowerCase() ? 'text-white/80' : 'text-slate-400'}`}>
                      {count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
